import {
  type CellClickedEvent,
  type ColDef,
  type ColGroupDef,
  type DefaultMenuItem,
  type FilterModel,
  type GetContextMenuItemsParams,
  type GetMainMenuItemsParams,
  type GridOptions,
  type GridReadyEvent,
  type IDetailCellRendererParams,
  type IRowNode,
  type IsRowFilterable,
  type IsRowPinned,
  type IsRowSelectable,
  type MenuItemDef,
  type ProcessCellForExportParams,
  type ProcessRowGroupForExportParams,
  type RowSelectionOptions,
  SELECTION_COLUMN_ID,
  SortChangedEvent,
} from "ag-grid-community"
import { type AgGridReact } from "ag-grid-react"
import { addDays } from "date-fns"
import { useFlags } from "~/features/feature-flags"
import { isMacOs } from "react-device-detect"
import { startCase } from "lodash"
import { useRouter } from "@tanstack/react-router"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  AggregationMethod,
  AggregationSelector,
  defaultAggregations,
} from "~/components/reusable/AggregationSelector"
import { DataTableBaseV2, type SortColumn } from "~/components/reusable/DataTableV2"
import { HideColumnItem } from "~/components/reusable/table/menuItems/HideColumnItem"
import { useForecastMetadata, useForecasts } from "~/hooks/useForecasts"
import { useSaveMetadata } from "~/hooks/useMetadata"
import {
  useCollectionSkuExtraDataKeys,
  useExtraDataAggregationMap,
  useExtraDataDisplayNameMap,
  useExtraDataFormattedKeys,
  useExtraDataMetadata,
  useExtraDataTypeMap,
  useUpdateSkuExtraDataMetadata,
} from "~/hooks/useSku"
import { useDisabledColumnIdSet } from "~/hooks/useDisabledColumns"
import { trackEvent } from "~/hooks/useTrackingService"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { cn } from "~/utils/helpers"
import {
  clampEditable,
  getFilteredLeafChildren,
  groupRowExportValue,
  isAutoGroupColumn,
  leafRowIds,
} from "~/utils/ag-utils"
import { SupplyTableColumn } from "~/constants/supplyPlan"
import { useErpLockedFields } from "~/hooks/useErpLockedFields"
import { ERP_CONTROLLED_TOOLTIP, SKU_METADATA_COLUMN_TO_ERP_FIELD } from "~/constants/erpFields"
import { openProduct } from "~/utils/routerUtils"
import { EMPTY_ARRAY, filterDefined } from "~/utils/tsUtils"
import { selectBestImageUrlFromObjects } from "~/utils/imageUtils"

import { dynamicTableColumnsV2 } from "../dynamicTableData/DynamicTableColumns"
import { isStorageLocationColId } from "~/utils/storageLocationColumns"
import { ExtraDataTable, getExtraDataDisplayName } from "~/utils/dynamicTableUtils"
import { getTableColumns } from "./ColumnChooser"
import ProductTableColumnChooser from "./ProductTableColumnChooser"
import { headerNamesByColId } from "~/components/planning/columnChooser/columnChooserModel"
import { useRegisterTable, useTableHasConsumer } from "~/components/planning/TableRegistryContext"
import { useOpenColumnChooserCommand } from "~/components/planning/columnChooser/openColumnChooser"
import {
  detailedSkuColumns,
  effectiveDefaultColumnIds,
  getNewAggregations,
  isCalendarColumn,
  ProductTableColumn,
  resolveDefaultAggregations,
} from "~/utils/productTableUtils"
import { useUserLookup } from "~/hooks/useUsers"
import { useCompany } from "~/hooks/useCompany"
import useShowHelix from "~/hooks/useShowHelix"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { IDENTITY_COLUMN_SPECS, type IdentityColumnCtx } from "./columns/identity"
import { COLLECTION_COLUMN_SPECS, type CollectionColumnCtx } from "./columns/collection"
import {
  FORECAST_COLUMN_SPECS,
  type ForecastColumnCtx,
  type NetForecastValueGetter,
} from "./columns/forecast"
import { LIFECYCLE_COLUMN_SPECS, type LifecycleColumnCtx } from "./columns/lifecycle"
import { SALES_COLUMN_SPECS, type SalesColumnCtx } from "./columns/sales"
import { STOCK_COLUMN_SPECS, type StockColumnCtx } from "./columns/stock"
import { SUPPLY_COLUMN_SPECS, type MetadataEditor, type SupplyColumnCtx } from "./columns/supply"
import { createColDefArrayCache, createColDefCache } from "./columns/reuse"
import { resolveColumnSpecs, resolveColumnSpecsById } from "./columns/types"
import {
  ACTIONS_COLUMN_SPECS,
  type ActionsColumnCtx,
  type DelayedPoWarning,
} from "./columns/actions"
import { RULES_COLUMN_SPECS, type RulesColumnCtx } from "./columns/rules"
import {
  ArrowSquareOut,
  ArrowsIn,
  ArrowsOutSimple,
  FunnelSimple,
  SidebarSimple,
  Warning,
} from "@phosphor-icons/react"
import { useGetCollections } from "~/hooks/useCollections"
import { Tooltip } from "~/components/reusable/Tooltip"
import { DEFAULT_FORECAST_ALERT_THRESHOLD } from "~/components/settings/ForecastAlertThresholdCard"
import CustomAgGridTooltip from "~/components/reusable/table/tooltips/CustomTooltip"
import { FlagsActionDialog } from "~/components/planning/ProductTable/FlagsActionDialog"
import { PreorderWhyDialogHost } from "~/components/reusable/cards/PreorderWhyDialogHost"
import { IMPUTATION_WARNING_THRESHOLD } from "~/utils/sizeSplitConfidence"
import { COLLECTION_TABLE_COLUMN_IDS } from "~/constants/collections"
import { Button, IconButton } from "~/components/design-system/components"
import { ProductTableType, type NpiTabContext } from "~/constants/productTable"
import { useProductTableAggregations } from "~/hooks/useProductTable"
import { useStableValue } from "~/hooks/useStableValue"
import { useTableSearch } from "~/hooks/useTableSearch"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import { FilterListPopover } from "~/components/planning/FilterListPopover"
import {
  type Filter,
  type FilterFieldType,
  filterVariants,
  compareString,
  reseedFilters,
} from "~/utils/filter"
import { useRuleById } from "~/components/rules/ruleColors"
import type { VariantRuleMatch } from "~/utils/variantRules"
import { useFilterFieldTypeResolver } from "~/hooks/useFilterFieldTypeResolver"
import { useFilters } from "~/hooks/useFilters"
import { dequal } from "dequal"
import { useEventCallback, useSessionStorage } from "usehooks-ts"
import useCollectionTableData from "~/hooks/useCollectionTableData"
import { useCollectionTableColumns } from "~/hooks/useCollectionTableColumns"
import type { VersionedColumns } from "~/hooks/useRefreshColumnsOnDataChange"
import { ConfigureExtradataColumn } from "~/components/reusable/table/menuItems/ConfigureExtradataColumn"
import { ConfigureExtradataDialog } from "~/components/collections/dialogs/ConfigureExtradataDialog"
import { useVariantChannelData } from "~/hooks/useVariantChannelData"
import { useEnsureProductTableSecondaryData } from "~/hooks/useVariantTableHooks"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import { createGroupedStringColumn } from "~/utils/columnTemplates"
import { useColumnAvailability } from "./utils/columnAvailability"
import { migrateProductTableState } from "./utils/columnPlacement"
import { clearHiddenColumnSortAndFilter } from "./utils/clearHiddenColumnSortAndFilter"
import {
  aggFuncName,
  type ColumnVisibility,
  commitDisplayedColumns,
  PRODUCT_TABLE_AGG_FUNCS,
  colIdsOf,
  reconcileAggregation,
  resolveColumnVisibility,
} from "./utils/columnVisibility"
import {
  columnShownReader,
  useAggregationFollowsVisibility,
  useFollowDefaultColumns,
  useHideColumn,
  useNewColumnPlacement,
  useProductTableColumnIds,
  useShownColumnIds,
  useViewColumnCheck,
} from "./useProductTableColumns"
import { HIGHLIGHT_ROW_CLASS, SelectionHighlightSync, useRowHighlight } from "./useRowHighlight"
import { useProductTableGridContext } from "./useProductTableGridContext"
import { useSkuMasterDetail } from "./useSkuMasterDetail"
import { menuIcon } from "./utils/menuIcon"
import { skuIdentityColumnDefs } from "./utils/skuDetailColumns"
import { columnIdsInGroups, purchaseOrderColumnNaming } from "./utils/columnUtils"
import { type TableColumnGroups } from "~/constants/tableColumnGroups"
import { PurchaseOrderTableColumn } from "~/constants/purchaseOrderTable"

import { MultiEditPanel } from "./MultiEditPanel"
import {
  SelectedVariantsProvider,
  useSelectedVariantHandlers,
  useSelectedVariantHandlersOptional,
} from "~/context/SelectedVariantsProvider"
import {
  InspectorPeekCellButton,
  useHasInspector,
  useOpenInspector,
  useInspectorHighlightStep,
} from "~/components/design-system/pages"

import {
  CellProductImage,
  OpenCellButton,
  ProductCellRenderer,
  SkuCellRenderer,
  VariantCellRenderer,
} from "./ProductCellRenderers"
import { isNamedRowField, rowLabel, TOTALS_ROW_LABEL } from "./utils/rowLabels"
import { useVariants } from "~/hooks/useVariants"

// Re-export for backwards compatibility
export {
  CellProductImage,
  OpenCellButton,
  ProductCellRenderer,
  SkuCellRenderer,
  VariantCellRenderer,
}

export const localFiltersStorageKey = (tableId: string) => tableId + "-product-table-filters"

// Cap (px) for auto-sized SKU detail rows; scrolls internally past it.
const MAX_DETAIL_ROW_HEIGHT = 500

/**
 * Opt-in header grouping: a colDef whose `context.headerGroup` carries this marker is folded — together
 * with its CONSECUTIVE siblings sharing the same `groupId` — into one ColGroupDef just before render.
 * Everything upstream (visibility, `::` suffix mapping, saved column state, chooser) stays flat.
 */
export type HeaderGroupMarker = { groupId: string; headerName: string; headerTooltip?: string }

export type ProductTableV2Props = {
  includeFilter?: boolean
  /** Hides AG Grid's per-column filter icon in headers; column filters still back page filters. */
  hideHeaderFilterButtons?: boolean
  enableCellChangeFlash?: boolean
  enableRowPinning?: boolean
  id: string
  gridRef?: React.RefObject<AgGridReact<IVariantSummary> | null>
  type?: ProductTableType
  variants: IVariantSummary[]
  initialSortColumn?: SortColumn
  /** Default filter model to apply when the grid initializes (if no user filter is set) */
  defaultFilterModel?: FilterModel
  initialAggregation?: AggregationMethod
  /** False keeps `initialAggregation` even when the company sets a default grouping level. */
  followCompanyAggregationDefault?: boolean
  aggregationOptions?: AggregationMethod[]
  defaultColumnIdsToDisplay?: (ProductTableColumn | string)[]
  /**
   * Subset of `defaultColumnIdsToDisplay` that is data-derived (columns appear when
   * backend data changes, not from a user action). These are never auto-appended to
   * an already-customized column selection, so they can't leak into applied saved
   * views; the plain default still follows them.
   */
  defaultColumnIdsExcludedFromAutoAdd?: string[]
  overwriteColumnsToShow?: ProductTableColumn[]
  height?: string
  minHeight?: number
  warehouseId?: string | undefined
  channel?: string | undefined
  selectableRows?: boolean
  /**
   * Columns that read data outside `rowData` come as `VersionedColumns`, so they repaint when that
   * data changes while their definitions stay stable.
   */
  extraColumns?: ColDef<IVariantSummary>[] | VersionedColumns<IVariantSummary>
  /**
   * When true, the Demand column renders return-adjusted (net) values and renames to
   * "Net Demand". Owned by the caller (Forecast Updates) so this shared table stays generic.
   */
  netForecastMode?: boolean
  /** Scales a variant's gross demand to net. Returns null while net factors load. */
  netForecastValueGetter?: NetForecastValueGetter
  /** True while net factors are loading — Demand cells/totals render "-" instead of gross. */
  netForecastPending?: boolean
  onClickPrimaryCell?: (
    skus: IVariantSummary[],
    event: CellClickedEvent<IVariantSummary>,
    newTab: boolean,
  ) => void
  /**
   * When true, clicking the primary cell selects that row (plain click replaces, ⌘/Ctrl toggles),
   * so the grid's row selection can drive inspector flows. Off by default.
   */
  selectOnPrimaryClick?: boolean
  /**
   * When true, the Module inspector details the highlighted rows: clicking a row (or moving cell
   * focus onto it) highlights that row and releases the checkboxes, and ticking checkboxes
   * highlights the ticked rows. Prefer this over `selectOnPrimaryClick` for panel flows.
   */
  highlightOnRowClick?: boolean
  /** Highlighted variant ids, emitted whenever the highlight moves. */
  onHighlightedVariantsChange?: (variantIds: string[]) => void
  topRightComponent?: React.ReactNode
  topLeftComponent?: React.ReactNode
  masterDetail?: boolean
  detailCellRenderer?: React.FC<IDetailCellRendererParams<IVariantSummary>>
  overrideDetailCellRendererParams?: any
  detailRowAutoHeight?: boolean
  keepDetailRows?: boolean
  onSelectedVariantsChange?: (variantIds: string[]) => void
  loading?: boolean
  enableSearch?: boolean
  enableFilter?: boolean
  extraFilterOptions?: React.ReactNode
  /**
   * Show AG Grid's native per-column filter funnel on every column with
   * `cellDataType: "number"`. Only attaches `agNumberColumnFilter` when the
   * column doesn't already define a filter, so explicit per-column filters win.
   */
  enableNumberFilters?: boolean
  initialLocalFilters?: Filter[]
  reseedLocalFilterFields?: readonly Filter["field"][]
  extraFilterFields?: FilterFieldType[]
  groupAggFiltering?: boolean | IsRowFilterable
  isRowPinned?: IsRowPinned<IVariantSummary>
  isRowSelectable?: IsRowSelectable<IVariantSummary>
  isExpanded?: boolean
  onIsExpandedChange?: (isExpanded: boolean) => void
  onSortChanged?: (event: SortChangedEvent) => void
  onFilterChanged?: (event: any) => void
  collectionIds?: number[]
  collectionPeriod?: { startDate: Date; endDate: Date }
  isNoos?: boolean
  overwriteCollectionColumns?: boolean
  /**
   * Lists the collection columns in the Columns chooser while a collection filter is active; without
   * one they stay registered with the grid, hidden.
   */
  listCollectionColumnsInChooser?: boolean
  leftPinnedColumns?: string[]
  priorityColumns?: string[]
  customRowFieldId?: string
  customGridOptions?: GridOptions<IVariantSummary>
  onGridReady?: (event: GridReadyEvent<IVariantSummary>) => void
  tabContext?: NpiTabContext
  hideReorder?: boolean
  hideAdjust?: boolean
  grandTotalRowPosition?: "pinnedBottom" | "bottom"
  /**
   * The caller's own grid `context`, merged over the product table's. Pass it here rather than
   * `setGridOption("context")` alone: the table re-sends its context when warehouse or forecasts
   * change, which would drop anything set only on the grid.
   */
  context?: Record<string, unknown>
  /** Overrides the label cell of group rows that are neither product nor variant rows. */
  renderGroupRow?: (group: {
    label: string
    leafCount: number
    action: React.ReactNode
  }) => React.ReactNode
  delayedPoWarningMap?: Map<string, DelayedPoWarning>
  snoozedUntilMap?: Map<string, Date>
  onReviewSelected?: (variantIds: string[]) => void
  disableColumnSizingPersistence?: boolean
  startOnFirstPage?: boolean
  cardClassName?: string
  footerClassName?: string
  /** Rendered inside the table's card, under the grid (and its pinned Totals row). */
  cardFooter?: React.ReactNode
  /** Replaces the Totals row label's default styling. */
  totalsLabelClassName?: string
  /**
   * Hold the grouped identity column at its own width when the grid underfills its container.
   * `sizeColumnsToFit` shares the slack in proportion to current width, and this column starts the
   * widest, so on a table of few narrow columns it takes the largest share and opens far too wide.
   */
  suppressGroupColumnSizeToFit?: boolean
  groupColumnMinWidth?: number
  /**
   * When grouping by vendor, repeat a variant row once per VendorOnSku candidate instead of showing
   * it only under its sourcing vendor. Double-counts quantities across vendor groups (and costs the
   * table its grand total), so only use it where listing capability beats planning a buy.
   */
  expandRowsPerVendor?: boolean
  /**
   * Column-chooser groups to drop from this table: every registered column in these groups is
   * gated the same way flag- and setting-gated columns are, so it is neither shown nor offered.
   */
  hiddenColumnGroups?: readonly TableColumnGroups[]
  tileImages?: boolean
  /** Rule matches the page evaluated; the Rules column is only offered with them (Demand pages). */
  ruleMatches?: ReadonlyMap<string, readonly VariantRuleMatch[]>
}

const NO_RULE_MATCHES: ReadonlyMap<string, readonly VariantRuleMatch[]> = new Map()

const getProductTableSearchFields = () => [
  { value: "all", label: "All" },
  { value: "variant", label: "Variant" },
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "color", label: "Color" },
  { value: "sku", label: "SKU" },
]

/** Plain column arrays carry no data version, so only the versioned form repaints on data changes. */
function splitExtraColumns<TData>(
  extraColumns: ColDef<TData>[] | VersionedColumns<TData> | undefined,
): { columns: ColDef<TData>[] | undefined; versioned: VersionedColumns<TData> | undefined } {
  if (Array.isArray(extraColumns)) return { columns: extraColumns, versioned: undefined }
  return { columns: extraColumns?.columns, versioned: extraColumns }
}

/** A callback tooltip that yields nothing falls back to the description, like an unset one. */
function headerTooltipOrDescription<TData>(
  headerTooltip: ColDef<TData>["headerTooltip"],
  description: string,
): ColDef<TData>["headerTooltip"] {
  if (typeof headerTooltip !== "function") return headerTooltip ?? description
  return (params) => headerTooltip(params) ?? description
}

export default function ProductTableV2(props: ProductTableV2Props) {
  const internalGridRef = useRef<AgGridReact<IVariantSummary> | null>(null)
  const gridRef = props.gridRef ?? internalGridRef
  const existing = useSelectedVariantHandlersOptional()

  if (existing) {
    return <ProductTableV2Inner {...props} gridRef={gridRef} />
  }

  return (
    <SelectedVariantsProvider<IVariantSummary> gridRef={gridRef}>
      <ProductTableV2Inner {...props} gridRef={gridRef} />
      <FlagsActionDialog />
      <PreorderWhyDialogHost />
    </SelectedVariantsProvider>
  )
}

/**
 * Marks a control whose cell must not move the inspector highlight — an action on the row rather
 * than a request to detail it. Put it on the element or any ancestor inside the cell.
 */
const NO_HIGHLIGHT_ATTR = "data-no-highlight"

// Columns whose own reads wait until one of them is shown.
const GATING_COLUMN_IDS = [
  ProductTableColumn.ForecastQuality,
  ProductTableColumn.Rules,
  ...COLLECTION_TABLE_COLUMN_IDS,
]

function ProductTableV2Inner({
  includeFilter = false,
  enableCellChangeFlash = true,
  hideHeaderFilterButtons = false,
  enableRowPinning = true,
  id,
  gridRef,
  type = ProductTableType.Default,
  variants,
  expandRowsPerVendor = false,
  hiddenColumnGroups,
  tileImages = false,
  ruleMatches,
  initialAggregation,
  followCompanyAggregationDefault = true,
  aggregationOptions,
  initialSortColumn,
  defaultFilterModel,
  defaultColumnIdsToDisplay: defaultColumnIdsProp = EMPTY_ARRAY,
  defaultColumnIdsExcludedFromAutoAdd = EMPTY_ARRAY,
  overwriteColumnsToShow,
  height,
  minHeight = 700,
  warehouseId,
  channel,
  selectableRows = true,
  extraColumns: extraColumnsProp,
  netForecastMode = false,
  netForecastValueGetter,
  netForecastPending = false,
  onClickPrimaryCell,
  selectOnPrimaryClick = false,
  highlightOnRowClick = false,
  onHighlightedVariantsChange,
  topRightComponent,
  topLeftComponent,
  masterDetail,
  detailCellRenderer,
  overrideDetailCellRendererParams,
  detailRowAutoHeight = false,
  keepDetailRows = true,
  onSelectedVariantsChange,
  loading,
  enableSearch = false,
  enableFilter = false,
  extraFilterOptions,
  enableNumberFilters = false,
  initialLocalFilters,
  reseedLocalFilterFields,
  extraFilterFields,
  groupAggFiltering,
  isRowPinned,
  isRowSelectable,
  isExpanded,
  onIsExpandedChange,
  onSortChanged,
  onFilterChanged,
  collectionIds,
  collectionPeriod,
  isNoos = false,
  overwriteCollectionColumns: overrideCollectionColumns,
  listCollectionColumnsInChooser = false,
  leftPinnedColumns,
  priorityColumns,
  customRowFieldId,
  customGridOptions,
  onGridReady,
  tabContext,
  hideReorder,
  hideAdjust,
  grandTotalRowPosition = "pinnedBottom",
  context,
  renderGroupRow,
  delayedPoWarningMap,
  snoozedUntilMap,
  onReviewSelected,
  disableColumnSizingPersistence = false,
  startOnFirstPage = false,
  cardClassName,
  footerClassName,
  cardFooter,
  totalsLabelClassName,
  suppressGroupColumnSizeToFit = false,
  groupColumnMinWidth = 300,
}: ProductTableV2Props & {
  gridRef: React.RefObject<AgGridReact<IVariantSummary> | null>
}) {
  const router = useRouter()
  const { permissions } = useUser()
  const { isFieldLocked } = useErpLockedFields()
  // Warms the cache for the CellUser cells.
  useUserLookup()
  const [showInternalFeatures] = useShowInternalFeatures()
  // Warms the cache for IncomingOrderCellRenderer.
  api.warehouse.get.useQuery()
  const { clearSelectedVariants, getSelectedVariantIds } = useSelectedVariantHandlers()

  // Fetch secondary fields (forecastAlertAggregate, forecastedReturnsQuantity,
  // futureKnownPreorders) only when this table actually mounts — useVariantTableDataHeavy
  // observes the cache and merges them into the SKU store.
  useEnsureProductTableSecondaryData()

  const [isGridReady, setIsGridReady] = useState(false)
  const { highlightedIdsRef, highlightSubscription, highlightRows, fromSelection } =
    useRowHighlight({
      gridRef,
      highlightOnRowClick,
      onHighlightedVariantsChange,
      isGridReady,
    })

  const handleSelectionChangedLocal = useEventCallback(() => {
    onSelectedVariantsChange?.(getSelectedVariantIds())
  })

  // The in-cell peek control replaces the row's "open variant" button where the panel it would
  // reveal actually exists — a Module inspector plus this table driving its highlight. Without
  // both, the cell keeps its old open action.
  const hasInspector = useHasInspector()
  const peekEnabled = highlightOnRowClick && hasInspector
  const openInspector = useOpenInspector()

  const peekRow = useCallback(
    (rowId: string) => {
      const api = gridRef.current?.api
      // A group stands for its leaves: peeking one details every variant under it, which is the
      // multi state. Its own row id is never in the highlight — only leaves carry variants.
      const node = api && !api.isDestroyed?.() ? api.getRowNode(rowId) : null
      const leaves = leafRowIds([node])
      highlightRows(leaves.length > 0 ? leaves : rowId)
      if (api && !api.isDestroyed?.()) {
        // Focus is the highlight's input everywhere else, so keep the two in step — otherwise the
        // next arrow key resumes from whatever row was focused before.
        const column = api.getFocusedCell()?.column ?? api.getAllDisplayedColumns()[0]
        if (node?.rowIndex != null && column) api.setFocusedCell(node.rowIndex, column)
      }
    },
    [highlightRows, gridRef],
  )

  // Clearing only — the button collapses the panel itself, which is the half it owns.
  //
  // Goes through the published clear, not just the grid's own: where the page backs the
  // highlight with its own store (the Total view persists it), clearing the grid's ref alone is
  // overwritten by the controlled value on the next render and the row stays lit.
  const publishedClear = useInspectorHighlightStep().clearHighlight
  const unpeekRow = useCallback(() => {
    highlightRows(null)
    publishedClear?.()
    const api = gridRef.current?.api
    if (api && !api.isDestroyed?.()) api.clearFocusedCell()
  }, [highlightRows, gridRef, publishedClear])

  const { data: variantsChannelData, isLoading: isLoadingChannelData } = useVariantChannelData(
    variants,
    channel,
    warehouseId,
    channel !== undefined,
  )

  const filteredVariantsChannelData = useMemo(() => {
    if (variantsChannelData) {
      const variantIds = new Set(variants.map((variant) => variant.variantId))
      return variantsChannelData.filter((v: IVariantSummary) => variantIds.has(v.variantId))
    }
    return variantsChannelData
  }, [variantsChannelData, variants])

  const { data: collections } = useGetCollections()
  const { data: forecastMetadata } = useForecastMetadata()
  const { data: hasExternalPurchaseOrderCollectionId } =
    api.purchaseOrder.hasExternalCollectionId.useQuery()
  const { data: extradataColumns } = useExtraDataMetadata({
    tableNames: ["Sku", "CollectionOnSku"],
  })
  const updateExtraDataMetadata = useUpdateSkuExtraDataMetadata()
  const [aggregations, setAggregations] = useProductTableAggregations(
    id,
    initialAggregation,
    "session",
    followCompanyAggregationDefault,
  )
  const { data: company } = useCompany()
  const getFieldType = useFilterFieldTypeResolver()
  const { filteredCollections: globalFilteredCollections } = useFilters()

  const {
    searchText,
    setSearchText,
    searchBy,
    setSearchBy: setSearchField,
    effectiveSearchText,
  } = useTableSearch(id)
  const [localFilters, setLocalFilters] = useSessionStorage<Filter[]>(
    localFiltersStorageKey(id),
    [],
  )

  useEffect(() => {
    if (!initialLocalFilters) return
    if (reseedLocalFilterFields) {
      setLocalFilters((stored) =>
        reseedFilters(stored, initialLocalFilters, reseedLocalFilterFields),
      )
      return
    }
    if (initialLocalFilters.length > 0) setLocalFilters(initialLocalFilters)
  }, [initialLocalFilters])

  // Combine collectionIds prop with collection IDs from local + global filters.
  // The global `collections oneOf` filter (set via the page-level filter chip)
  // also has to flow in — otherwise extra-data cells/filters scope only to the
  // page prop and ignore the user's filter selection on pages like inventory-overview.
  const effectiveCollectionIds = useMemo(() => {
    const fromProp = collectionIds ?? []
    const collectionFilter = localFilters.find((f) => f.field === "collections")
    // Filter stores collection names, so we need to look up IDs
    const fromLocalFilter =
      collectionFilter?.values
        ?.map((name) => collections?.find((c) => c.name === name)?.id)
        .filter((id): id is number => id !== undefined) ?? []
    const fromGlobalFilter = globalFilteredCollections.map((c) => c.id)
    const combined = [...new Set([...fromProp, ...fromLocalFilter, ...fromGlobalFilter])]
    return combined.length > 0 ? combined : undefined
  }, [collectionIds, localFilters, collections, globalFilteredCollections])

  // Reset aggregation when effectiveCollectionIds becomes undefined and current aggregation uses collection extra data
  useEffect(() => {
    const usesCollectionExtraData = aggregations.some((agg) =>
      agg.includes("(collection-extradata)"),
    )
    if (!effectiveCollectionIds && usesCollectionExtraData) {
      setAggregations(
        resolveDefaultAggregations(initialAggregation, company?.defaultAggregationLevel),
      )
    }
  }, [
    effectiveCollectionIds,
    aggregations,
    initialAggregation,
    setAggregations,
    company?.defaultAggregationLevel,
  ])

  const { columns: extraColumns, versioned: extraVersionedColumns } =
    splitExtraColumns(extraColumnsProp)

  const extraColumnTitles = useMemo(() => headerNamesByColId(extraColumns), [extraColumns])

  const alwaysVisibleColumnIdSet = useMemo(
    () => new Set(colIdsOf((extraColumns ?? []).filter((c) => c.lockVisible))),
    [extraColumns],
  )
  const defaultColumnIdsToDisplay = useStableValue(
    effectiveDefaultColumnIds(defaultColumnIdsProp, overwriteColumnsToShow),
    dequal,
  )
  const shownGatingColumnIds = useShownColumnIds(id, GATING_COLUMN_IDS, defaultColumnIdsToDisplay)
  const isForecastQualityShown = shownGatingColumnIds.has(ProductTableColumn.ForecastQuality)
  const isCollectionColumnShown = !shownGatingColumnIds.isDisjointFrom(COLLECTION_TABLE_COLUMN_IDS)
  const [extradataDialogOpen, setExtradataDialogOpen] = useState<boolean>(false)
  const [extradataDialogColumn, setExtradataDialogColumn] = useState<any>()
  // Helix's forecasting paradigm has no forecast quality score.
  const showHelix = useShowHelix()
  const { data: evaluationMap } = api.forecastEvaluation.forecastEvaluationMap.useQuery(undefined, {
    enabled: !showHelix && isForecastQualityShown,
  })

  const isRulesColumnShown = shownGatingColumnIds.has(ProductTableColumn.Rules)
  const ruleById = useRuleById(isRulesColumnShown)
  const variantRuleMatches = ruleMatches ?? NO_RULE_MATCHES

  const { unfulfillablePreorders: unfulfillablePreordersFlag } = useFlags()

  const disabledColumnIdSet = useDisabledColumnIdSet()
  // Collection scope comes from the raw collectionIds prop, not the merged filter ids.
  const { hiddenColumnIds } = useColumnAvailability({
    isCollectionScoped: (collectionIds?.length ?? 0) > 0,
  })
  const gatedColumnIdKey = [
    ...hiddenColumnIds,
    ...columnIdsInGroups(hiddenColumnGroups ?? []),
    ...(showHelix ? [ProductTableColumn.ForecastQuality] : []),
    ...(ruleMatches ? [] : [ProductTableColumn.Rules]),
  ].join("|")
  const gatedColumnIdSet = useMemo(
    () => new Set<string>(gatedColumnIdKey ? gatedColumnIdKey.split("|") : []),
    [gatedColumnIdKey],
  )

  const saveMetadata = useSaveMetadata()
  // Bind the stable `mutateAsync` (the mutation object isn't) so the value-setter stays a stable colDefs dep.
  const { mutateAsync: saveMetadataAsync } = saveMetadata
  const tableColumnData = useMemo(() => getTableColumns(), [])

  const { getSkuForecastsBetweenDates } = useForecasts()
  const variantIds = useMemo(() => {
    return variants.map((variant) => variant.variantId)
  }, [variants])

  const { data: collectionMaps } = useCollectionTableData({
    variantIds,
    collectionIds: collectionIds ?? [],
    warehouseId,
    channel,
    // These collection-wide reads feed `collectionColumns` and nothing else, so they wait for one of
    // those columns to be shown — the same gate `forecastEvaluationMap` above uses.
    enabled: isCollectionColumnShown,
  })

  const versionedCollectionColumns = useCollectionTableColumns({
    maps: collectionMaps,
    showInDepthColumns: permissions.isStaff && showInternalFeatures,
    isNoos,
  })
  const collectionColumns = versionedCollectionColumns.columns
  const showCollectionColumns =
    !!collectionIds && collectionIds.length > 0 && !overrideCollectionColumns
  const registerCollectionColumns =
    showCollectionColumns || (listCollectionColumnsInChooser && !overrideCollectionColumns)

  // const variantsWithChannelData = useMemo(() => {
  //   if (variantsChannelData) {
  //     return variants.map((variant) => {
  //       const channelData = variantsChannelData?.find((v) => v.variantId === variant.variantId)
  //       return {
  //         ...variant,
  //         ...channelData,
  //       }
  //     })
  //   }
  //   return variants
  // }, [variants, variantsChannelData])

  // Kept apart from the search and filters so a keystroke keeps each row's object and AG Grid
  // only diffs the row set instead of updating every row.
  const groupedData = useMemo(() => {
    let data: typeof variants = variants
    if (filteredVariantsChannelData?.length > 0) {
      const channelDataMap = new Map(filteredVariantsChannelData.map((v) => [v.variantId, v]))
      // Channel rows already spread the variant, so they are used as is to keep their identity.
      data = variants.map((v) => channelDataMap.get(v.variantId) ?? v)
    }
    if (aggregations.includes(AggregationMethod.Vendor)) {
      // By default a variant belongs to exactly one vendor group — its sourcing vendor, already
      // resolved into `groupVendor`. Repeating the row once per VendorOnSku candidate counted a
      // multi-vendor variant's quantities under every vendor (ING-843). Opt back in only where
      // listing a variant under every capable vendor is the point.
      if (expandRowsPerVendor) {
        data = data.flatMap((item) => [
          item,
          ...item.vendors.slice(1, item.vendors.length).map((vendor) => ({
            ...item,
            groupVendor: vendor?.name,
          })),
        ])
      }
    } else if (aggregations.includes(AggregationMethod.Collection)) {
      data = data.flatMap((item) => {
        let collections = item.collections
        if (collectionIds && collectionIds.length > 0) {
          collections = collections?.filter((c) =>
            !isNaN(Number(c.id)) ? collectionIds.includes(Number(c.id)) : true,
          )
        }
        return [
          ...collections?.map((collection) => ({
            ...item,
            groupCollection: collection?.name,
          })),
        ]
      })
    }
    return data
  }, [variants, aggregations, expandRowsPerVendor, filteredVariantsChannelData, collectionIds])

  const transformedData = useMemo(() => {
    let data = groupedData
    // applyFilter returns whole variantId groups, not individual rows — filter by groupVendor instead.
    if (aggregations.includes(AggregationMethod.Vendor) && enableFilter) {
      const vendorFilters = localFilters.filter((f) => f.field === "vendors")
      if (vendorFilters.length > 0) {
        data = data.filter((item) =>
          vendorFilters.every((f) => {
            if (!item.groupVendor) return false
            return compareString(item.groupVendor, f.operator, f.value, f.values)
          }),
        )
      }
    }

    // Exclude vendor filters when vendor-grouped: already handled row-by-row above.
    const filtersForVariants = !enableFilter
      ? []
      : aggregations.includes(AggregationMethod.Vendor)
        ? localFilters.filter((f) => f.field !== "vendors")
        : localFilters
    return filterVariants(
      filtersForVariants,
      data,
      effectiveSearchText,
      getFieldType,
      effectiveCollectionIds,
    )
  }, [
    groupedData,
    aggregations,
    effectiveSearchText,
    enableFilter,
    localFilters,
    effectiveCollectionIds,
    getFieldType,
  ])

  // Vendor grouping repeats a variant row once per extra vendor (see transformedData above), so a
  // grand total over the leaf rows would count those variants twice. Suppress it only when that
  // duplication actually happened — most tenants have a single vendor per variant and are entitled
  // to their totals row (SLIM-593).
  const hasDuplicatedVendorRows = useMemo(
    () =>
      expandRowsPerVendor &&
      aggregations.includes(AggregationMethod.Vendor) &&
      variants.some((variant) => (variant.vendors?.length ?? 0) > 1),
    [expandRowsPerVendor, aggregations, variants],
  )

  const { data: extraDataFields } = useExtraDataFormattedKeys()
  const { data: collectionExtraDataFields } = useCollectionSkuExtraDataKeys()

  const { data: extraDataColTypeMap } = useExtraDataTypeMap()
  const { data: extraDataAggregationMap } = useExtraDataAggregationMap()
  const { data: extraDataDisplayNameMap } = useExtraDataDisplayNameMap()
  const forecastAlertThreshold = company?.forecastAlertThreshold ?? DEFAULT_FORECAST_ALERT_THRESHOLD

  const thresholdDay = useMemo(
    () => addDays(getStartOfCurrentWeekUTC(), company?.overstockThresholdDays ?? 180),
    [company?.overstockThresholdDays],
  )

  const dynamicColumns = useMemo(
    () =>
      dynamicTableColumnsV2(
        extraDataFields ?? [],
        collectionExtraDataFields ?? [],
        extraDataDisplayNameMap,
        extraDataColTypeMap,
        extraDataAggregationMap,
        effectiveCollectionIds,
      ),
    [
      extraDataFields,
      collectionExtraDataFields,
      extraDataDisplayNameMap,
      extraDataColTypeMap,
      extraDataAggregationMap,
      effectiveCollectionIds,
    ],
  )

  const storeVariants = useVariants()
  const storeVariantsRef = useRef(storeVariants)
  useEffect(() => {
    storeVariantsRef.current = storeVariants
  }, [storeVariants])
  const openForecastView = useCallback(
    (variants: IVariantSummary[], newTab: boolean) => {
      const productId = variants[0]!.productId
      const fullVariantCount = storeVariantsRef.current.filter(
        (v) => v.productId === productId,
      ).length
      const isAllVariants =
        fullVariantCount > 0 && new Set(variants.map((v) => v.variantId)).size === fullVariantCount
      openProduct(productId, isAllVariants ? [] : variants.map((v) => v.variantId), router, newTab)
    },
    [router],
  )

  const handleAggregationChange = useCallback(
    (aggregation: AggregationMethod, level: number = 0) => {
      trackEvent("Aggregation Method Changed", {
        "Aggregation Method": aggregation,
        "Aggregation Level": level,
        "Table ID": id,
      })

      if (level === 0) {
        setAggregations(getNewAggregations(aggregation))
      } else if (level === 1 && aggregations[0]) {
        if (aggregation === AggregationMethod.Variant) {
          setAggregations([aggregations[0], aggregation])
        } else {
          setAggregations([aggregations[0], aggregation, AggregationMethod.Variant])
        }
      }

      clearSelectedVariants()
    },
    [id, aggregations, setAggregations, clearSelectedVariants],
  )

  const handleCellClicked = useCallback(
    (params: CellClickedEvent<IVariantSummary>) => {
      const event = params.event as PointerEvent
      const newTab = event?.metaKey || event?.ctrlKey
      const leafNodes = params.node.allLeafChildren ?? [params.node]
      const entries = leafNodes
        ?.map((node: IRowNode<IVariantSummary>) => node.data)
        .filter(Boolean) as IVariantSummary[]

      if (!entries) return

      // Primary-cell click drives row selection (⌘/Ctrl toggles, plain click replaces);
      // groupSelects "descendants" selects a group's leaves.
      if (selectOnPrimaryClick) {
        if (newTab) params.node.setSelected(!params.node.isSelected())
        else params.node.setSelected(true, true)
      }

      onClickPrimaryCell?.(entries ?? [], params, newTab)
    },
    [onClickPrimaryCell, selectOnPrimaryClick],
  )

  const isEditable = useCallback((params: any) => {
    return !params.node.footer
  }, [])

  const createMetadataValueSetter = useCallback(
    (fieldName: keyof Parameters<typeof saveMetadataAsync>[0], overrideNewValue?: any) => {
      return (params: any) => {
        const oldValue = params.oldValue?.value ?? params.oldValue
        const newValue = overrideNewValue ?? params.newValue

        if (!params.node || oldValue === newValue) return false

        const skuIds = params.node.group
          ? (params.node.allLeafChildren?.flatMap((child: any) => child.data?.skuIds) ?? [])
          : [...params.data.skuIds]

        if (skuIds.length > 0) {
          saveMetadataAsync({
            skuIds,
            [fieldName]: newValue,
          })
          return true
        }

        return false
      }
    },
    [saveMetadataAsync],
  )

  // A locked column overrides the template's own tooltip and tooltipComponent so the
  // cell explains the lock.
  const metadataEditor = useCallback<MetadataEditor>(
    (fieldName, editable) => {
      const erpField = SKU_METADATA_COLUMN_TO_ERP_FIELD[fieldName]
      if (erpField !== undefined && isFieldLocked(erpField)) {
        return {
          valueSetter: createMetadataValueSetter(fieldName),
          editable: false,
          tooltip: () => ({ unavailableMessage: ERP_CONTROLLED_TOOLTIP }),
          tooltipComponent: CustomAgGridTooltip,
        }
      }
      return { valueSetter: createMetadataValueSetter(fieldName), editable }
    },
    [createMetadataValueSetter, isFieldLocked],
  )

  // Reused across rebuilds so ag-grid can skip unchanged columns, and the array with them.
  const [reuseColDef] = useState(() => createColDefCache<IVariantSummary>())
  const [reuseColDefs] = useState(() => createColDefArrayCache<IVariantSummary>())

  const supplyColDefs = useMemo(
    () =>
      resolveColumnSpecs(SUPPLY_COLUMN_SPECS, {
        isEditable,
        metadataEditor,
        currency: company?.currency,
        includeFilter,
        warehouseId,
        aggregations,
      } satisfies SupplyColumnCtx),
    [isEditable, metadataEditor, company?.currency, includeFilter, warehouseId, aggregations],
  )

  const identityColumn = useMemo(
    () =>
      resolveColumnSpecsById(IDENTITY_COLUMN_SPECS, {
        includeFilter,
        aggregations,
        groupAggFiltering,
      } satisfies IdentityColumnCtx),
    [includeFilter, aggregations, groupAggFiltering],
  )

  const stockColumn = useMemo(
    () =>
      resolveColumnSpecsById(STOCK_COLUMN_SPECS, {
        currency: company?.currency,
        thresholdDay,
        collections,
        collectionIds,
        collectionPeriod,
        hasExternalPurchaseOrderCollectionId,
      } satisfies StockColumnCtx),
    [
      company?.currency,
      thresholdDay,
      collections,
      collectionIds,
      collectionPeriod,
      hasExternalPurchaseOrderCollectionId,
    ],
  )

  const lifecycleColumn = useMemo(
    () =>
      resolveColumnSpecsById(LIFECYCLE_COLUMN_SPECS, {
        isEditable,
        metadataEditor,
        groupAggFiltering,
      } satisfies LifecycleColumnCtx),
    [isEditable, metadataEditor, groupAggFiltering],
  )

  const salesColumn = useMemo(
    () =>
      resolveColumnSpecsById(SALES_COLUMN_SPECS, {
        currency: company?.currency,
      } satisfies SalesColumnCtx),
    [company?.currency],
  )

  const alertsUnavailable = !!channel || !!warehouseId
  const forecastColumn = useMemo(
    () =>
      resolveColumnSpecsById(FORECAST_COLUMN_SPECS, {
        unfulfillablePreordersFlag,
      } satisfies ForecastColumnCtx),
    [unfulfillablePreordersFlag],
  )

  const collectionColumn = useMemo(
    () =>
      resolveColumnSpecsById(COLLECTION_COLUMN_SPECS, {
        aggregations,
        collectionIds,
      } satisfies CollectionColumnCtx),
    [aggregations, collectionIds],
  )

  const actionsColumn = useMemo(
    () =>
      resolveColumnSpecsById(ACTIONS_COLUMN_SPECS, {
        collections,
        phasingOutSellDownForecast: company?.phasingOutSellDownForecast,
        snoozedUntilMap,
        delayedPoWarningMap,
      } satisfies ActionsColumnCtx),
    [collections, company?.phasingOutSellDownForecast, snoozedUntilMap, delayedPoWarningMap],
  )

  const rulesColumn = useMemo(
    () =>
      resolveColumnSpecsById(RULES_COLUMN_SPECS, {
        matches: variantRuleMatches,
        ruleById,
      } satisfies RulesColumnCtx),
    [variantRuleMatches, ruleById],
  )

  const handleConfigureExtradataColumn = useCallback(
    (columnId: string | undefined) => {
      if (columnId == null || extradataColumns == null) {
        return
      }
      const columnConfig = extradataColumns.find(
        (val: any) =>
          columnId.split("(extradata)")[0] === val.fieldName ||
          columnId.split("(collection-extradata)")[0] === val.fieldName,
      )

      setExtradataDialogColumn(columnConfig)
      setExtradataDialogOpen(true)
    },
    [extradataColumns],
  )

  const handleUpdateExtradataConfiguration = (extradataConfiguration: {
    tableName: string
    fieldName: string
    userSetAggregation: string | null
    userSetDisplayName: string | null
  }) => updateExtraDataMetadata.mutate([extradataConfiguration])

  const autoGroupColumnDef: ColDef = useMemo(() => {
    return createGroupedStringColumn({
      minWidth: groupColumnMinWidth,
      initialWidth: 300,
      suppressSizeToFit: suppressGroupColumnSizeToFit,
      field: "variantId",
      onCellClicked: onClickPrimaryCell || selectOnPrimaryClick ? handleCellClicked : undefined,
      headerName: aggregations[0]?.includes("(collection-extradata)")
        ? getExtraDataDisplayName(
            aggregations[0]?.replace("(collection-extradata)", ""),
            ExtraDataTable.COLLECTION_ON_SKU,
            extraDataDisplayNameMap,
          )
        : aggregations[0]?.includes("(extradata)")
          ? getExtraDataDisplayName(
              aggregations[0]?.replace("(extradata)", ""),
              ExtraDataTable.SKU,
              extraDataDisplayNameMap,
            )
          : startCase(aggregations[0] ?? ""),
      cellRendererSelector: () => {
        return {
          component: "agGroupCellRenderer",
          params: {
            suppressCount: true,
            innerRenderer: (params: any) => {
              const footer = params.node.footer
              if (footer) {
                return (
                  <span className={totalsLabelClassName ?? "font-semibold text-fg-subtle"}>
                    {TOTALS_ROW_LABEL}
                  </span>
                )
              }

              const field = params.node.field
              const group = params.node.group

              const openRow = (newTab: boolean) => {
                const leafNodes = params.node.allLeafChildren ?? [params.node]
                const entries = leafNodes
                  ?.map((n: any) => n.data)
                  .filter(Boolean) as IVariantSummary[]
                if (entries.length > 0) openForecastView(entries, newTab)
              }
              const createOnOpen = () => (e: React.MouseEvent) => openRow(e.metaKey || e.ctrlKey)

              // Where an inspector is there to peek into, the row's action becomes the peek
              // toggle joined to "open variant".
              //
              // Group rows too: a group stands for its leaves, so peeking one details every variant
              // under it. It carries no variant of its own, so the highlight it reads — and sets —
              // is its leaves', which is also what makes its button read "Close" while they are on
              // show. A group whose leaves have no row ids (nothing to highlight) keeps the open
              // action.
              const rowId: string | null = params.node.id ?? null
              // Behind `peekEnabled`: walking a group's leaves is the one non-trivial thing this
              // renderer does, and a table with no inspector must not pay for it on every repaint.
              const highlightIds: string[] | undefined =
                peekEnabled && group
                  ? filterDefined(getFilteredLeafChildren(params.node).map((n) => n.id))
                  : undefined
              const action =
                peekEnabled && rowId != null && (!group || (highlightIds?.length ?? 0) > 0) ? (
                  <InspectorPeekCellButton
                    rowId={rowId}
                    highlightIds={highlightIds}
                    subscription={highlightSubscription}
                    onOpen={peekRow}
                    onClose={unpeekRow}
                    onOpenElsewhere={isNamedRowField(field) || !group ? openRow : undefined}
                    rowNode={params.node}
                  />
                ) : undefined

              if (isNamedRowField(field)) {
                return (
                  <ProductCellRenderer
                    {...params}
                    aggregations={aggregations}
                    onOpen={action ? undefined : createOnOpen()}
                    action={action}
                    tile={tileImages}
                  />
                )
              } else if (!group) {
                return (
                  <VariantCellRenderer
                    {...params}
                    aggregations={aggregations}
                    onOpen={action ? undefined : createOnOpen()}
                    action={action}
                    tile={tileImages}
                  />
                )
              }

              const entries = filterDefined(params.node?.allLeafChildren?.map((n: any) => n.data))
              const imageUrl = selectBestImageUrlFromObjects(entries) ?? ""
              const stringValue = Array.isArray(params.value)
                ? params.value.join(", ")
                : params.value
              if (renderGroupRow) {
                return renderGroupRow({ label: stringValue, leafCount: entries.length, action })
              }
              // Any other grouping (vendor, collection, an extra-data field). It gets the same peek
              // action as a product group — the inspector answers "these variants" whatever the rows
              // were grouped by — pushed right so the label keeps the left edge.
              // Only stretched when it carries an action: tables without an inspector keep the
              // label sized to its content, as it was before the peek button reached group rows.
              return action ? (
                <div className="flex items-center gap-3 leading-5 h-[38px] w-[100%]">
                  <CellProductImage imageUrl={imageUrl} tile={tileImages} />
                  {/* flex-1 pushes the action to the right edge, as both cell renderers do. */}
                  <span className="label-base flex-1 truncate">{stringValue}</span>
                  {action}
                </div>
              ) : (
                <div className="flex items-center gap-3 leading-5 h-[38px]">
                  <CellProductImage imageUrl={imageUrl} tile={tileImages} />
                  <span className="label-base">{stringValue}</span>
                </div>
              )
            },
          },
        }
      },
    })
  }, [
    aggregations,
    extraDataDisplayNameMap,
    onClickPrimaryCell,
    selectOnPrimaryClick,
    handleCellClicked,
    openForecastView,
    peekEnabled,
    highlightSubscription,
    peekRow,
    unpeekRow,
    suppressGroupColumnSizeToFit,
    groupColumnMinWidth,
    renderGroupRow,
    totalsLabelClassName,
    tileImages,
  ])

  const colDefs: ColDef<IVariantSummary>[] = useMemo(() => {
    const baseColDefs: ColDef<IVariantSummary>[] = [
      identityColumn(ProductTableColumn.ProductName),
      identityColumn(ProductTableColumn.Product),
      identityColumn(ProductTableColumn.ProductId),
      identityColumn(ProductTableColumn.VariantId),
      identityColumn(ProductTableColumn.VariantName),
      actionsColumn(ProductTableColumn.Flags),
      rulesColumn(ProductTableColumn.Rules),
      actionsColumn(ProductTableColumn.Adjustments),
      identityColumn(ProductTableColumn.Variant),
      identityColumn(ProductTableColumn.SKU),
      identityColumn(ProductTableColumn.Tags),
      identityColumn(ProductTableColumn.Planner),
      identityColumn(ProductTableColumn.Color),
      identityColumn(ProductTableColumn.BaseColor),
      identityColumn(ProductTableColumn.StockStatus),
      actionsColumn(ProductTableColumn.OptimalOrderDate),
      actionsColumn(ProductTableColumn.ExistingPO),
      identityColumn(ProductTableColumn.Size),
      identityColumn(ProductTableColumn.SizeDimension1),
      identityColumn(ProductTableColumn.SizeDimension2),
      identityColumn(ProductTableColumn.SizeSplitConfidence),
      identityColumn(ProductTableColumn.Brand),
      identityColumn(ProductTableColumn.Category),
      identityColumn(ProductTableColumn.Gender),
      identityColumn(ProductTableColumn.Fabric),
      salesColumn(ProductTableColumn.SuggestedOrder),
      forecastColumn(ProductTableColumn.ForecastModel),
      salesColumn(ProductTableColumn.SalesLast30Days),
      salesColumn(ProductTableColumn.Sales),
      salesColumn(ProductTableColumn.NetSales),
      salesColumn(ProductTableColumn.SalesWithoutPreOrders),
      forecastColumn(ProductTableColumn.Forecast),
      salesColumn(ProductTableColumn.Returns),
      salesColumn(ProductTableColumn.ExpectedReturns),
      salesColumn(ProductTableColumn.ReturnRate),
      forecastColumn(ProductTableColumn.PreOrders),
      salesColumn(ProductTableColumn.PreOrdersLastYear),

      salesColumn(ProductTableColumn.Revenue),
      salesColumn(ProductTableColumn.Profit),
      salesColumn(ProductTableColumn.SalesGrowth),
      forecastColumn(ProductTableColumn.ForecastedGrowth),
      forecastColumn(ProductTableColumn.ForecastedGrowthUnits),
      forecastColumn(ProductTableColumn.ForecastedGrowthAlert),
      forecastColumn(ProductTableColumn.ForecastInfo),
      forecastColumn(ProductTableColumn.ForecastSalesVsLy1m),
      forecastColumn(ProductTableColumn.ForecastSalesVsLy3m),
      forecastColumn(ProductTableColumn.ForecastSalesVsLy6m),
      forecastColumn(ProductTableColumn.ForecastSalesVsLy12m),
      identityColumn(ProductTableColumn.ABC),
      lifecycleColumn(ProductTableColumn.LifecycleStatus),
      stockColumn(ProductTableColumn.StockLevel),
      stockColumn(ProductTableColumn.ReservedStockLevel),
      stockColumn(ProductTableColumn.AvailableToSell),
      stockColumn(ProductTableColumn.IncomingStock),
      stockColumn(ProductTableColumn.LocationMinimumStockLevel),
      stockColumn(ProductTableColumn.InventoryValue),
      stockColumn(ProductTableColumn.LostSales),
      stockColumn(ProductTableColumn.AvailabilityWithinLeadTime),
      stockColumn(ProductTableColumn.AvailabilityToday),
      stockColumn(ProductTableColumn.LocationAvailability),
      stockColumn(ProductTableColumn.OverstockQuantity),
      stockColumn(ProductTableColumn.OverstockValue),
      stockColumn(ProductTableColumn.WeeksOnHand),
      stockColumn(ProductTableColumn.OutOfStockDate),
      ...supplyColDefs,
      salesColumn(ProductTableColumn.SellThroughRate),
      salesColumn(ProductTableColumn.InventoryTurn),
      salesColumn(ProductTableColumn.GMROI),
      identityColumn(ProductTableColumn.ProductType),
      lifecycleColumn(ProductTableColumn.ActivatedFrom),
      lifecycleColumn(ProductTableColumn.ActivatedUntil),
      lifecycleColumn(ProductTableColumn.PhasingOutFrom),
      forecastColumn(ProductTableColumn.ForecastQuality),
      collectionColumn(ProductTableColumn.Collection),
      forecastColumn(ProductTableColumn.PreOrderFulfillment),
      forecastColumn(ProductTableColumn.UnfulfillablePreorder),
      forecastColumn(ProductTableColumn.AtRiskPreorder),
      forecastColumn(ProductTableColumn.ForecastWithoutKnownPreOrders),
      forecastColumn(ProductTableColumn.ForecastWithoutPreOrders),
      forecastColumn(ProductTableColumn.GrowthWithoutPreOrders),
      forecastColumn(ProductTableColumn.StockoutImputation),
      forecastColumn(ProductTableColumn.MissingSalesImputation),
      // createStringColumn({
      //   colId: ProductTableColumn.ForecastBoundApplied,
      //   headerName: "Forecast bound applied",
      //   filter: "agSetColumnFilter",
      //   valueGetter: (p) => {
      //     const decomposition = p.data?.forecastDecomposition
      //     if (!decomposition) return undefined
      //     return decomposition.capApplied ? "Yes" : "No"
      //   },
      //   aggFunc: (params: IAggFuncParams) => {
      //     const entries: IVariantSummary[] = filterDefined(
      //       params.rowNode?.allLeafChildren?.map((node: any) => node.data),
      //     )
      //     const boundCount = entries.filter((e) => e.forecastDecomposition?.capApplied).length
      //     return boundCount > 0 ? `${boundCount} bounded` : ""
      //   },
      // }),
      ...(registerCollectionColumns ? collectionColumns : []),
      ...dynamicColumns,
      ...(extraColumns ?? []),
    ]

    const finalAggregations = aggregations
      //.filter((agg) => agg !== "Variant")
      .map((agg) => {
        if (agg.includes("(collection-extradata)")) {
          return getExtraDataDisplayName(
            agg.replace("(collection-extradata)", ""),
            ExtraDataTable.COLLECTION_ON_SKU,
            extraDataDisplayNameMap,
          ).toLowerCase()
        }
        if (agg.includes("(extradata)")) {
          return getExtraDataDisplayName(
            agg.replace("(extradata)", ""),
            ExtraDataTable.SKU,
            extraDataDisplayNameMap,
          ).toLowerCase()
        }
        return startCase(agg).toLowerCase()
      })

    const activeColDefs = resolveColumnVisibility(baseColDefs, {
      isShown: columnShownReader(id, new Set(defaultColumnIdsToDisplay)),
      disabledColumnIdSet,
      alwaysVisibleColumnIdSet,
      rowGroupHeaderNames: finalAggregations,
    })

    const mappedColDefs: ColDef<IVariantSummary>[] = activeColDefs.map<ColDef<IVariantSummary>>(
      ({ colDef, hide: shouldHide, aggregate, managed, required }) => {
        const pinned =
          colDef.pinned ?? (leftPinnedColumns?.includes(colDef.colId ?? "") ? "left" : undefined)

        const headerNameKey = colDef.headerName?.toLowerCase() ?? ""
        const rowGroup = finalAggregations.includes(headerNameKey)
        const rowGroupIndex =
          colDef.rowGroupIndex ??
          (finalAggregations.length > 1 ? finalAggregations.indexOf(headerNameKey) : 0)
        const description =
          tableColumnData.find((col) => col.id === colDef.colId)?.description ?? ""

        return reuseColDef(
          colDef.colId ?? "",
          [
            colDef,
            pinned,
            shouldHide,
            aggregate,
            managed,
            required,
            permissions.canEdit,
            rowGroup,
            rowGroupIndex,
            description,
          ],
          () => ({
            ...colDef,
            // Visibility is the grid's: a colDef only says how a new column starts.
            hide: undefined,
            initialHide: shouldHide,
            // Aggregation too: commitDisplayedColumns and reconcileAggregation write it from here on.
            aggFunc: undefined,
            initialAggFunc: aggregate
              ? (aggFuncName(colDef.aggFunc ?? colDef.initialAggFunc) ?? undefined)
              : undefined,
            context: {
              ...colDef.context,
              visibility: {
                managed,
                required,
                aggFunc: colDef.aggFunc ?? colDef.initialAggFunc,
              } satisfies ColumnVisibility,
            },
            editable: clampEditable(colDef, permissions.canEdit),
            rowGroup,
            rowGroupIndex,
            // Make sure pinned columns are left aligned
            pinned,
            headerTooltip: headerTooltipOrDescription(colDef.headerTooltip, description),
            headerClass: pinned === "left" ? "ag-left-pinned-header" : (colDef.headerClass ?? ""),
            cellStyle:
              typeof colDef.cellStyle === "function"
                ? (params: any) => {
                    const styleFn = colDef.cellStyle as (params: any) => any
                    const originalStyle = styleFn(params)
                    return {
                      ...originalStyle,
                      textAlign: pinned === "left" ? "left" : (originalStyle?.textAlign ?? ""),
                    }
                  }
                : {
                    ...colDef.cellStyle,
                    // @ts-ignore
                    textAlign: pinned === "left" ? "left" : (colDef.cellStyle?.textAlign ?? ""),
                  },
            headerComponentParams: {
              ...colDef.headerComponentParams,
              align: pinned === "left" ? "left" : (colDef.headerComponentParams?.align ?? "left"),
            },
          }),
        )
      },
    )

    if (priorityColumns) {
      // Move priority columns to beginning of array. Don't touch the rest of the array.
      const prioritySet = new Set(priorityColumns)
      const priorityCols = priorityColumns
        .map((colId) => mappedColDefs.find((col) => col.colId === colId))
        .filter((col): col is ColDef<IVariantSummary> => col != null)
      const rest = mappedColDefs.filter((col) => !prioritySet.has(col.colId!))
      return reuseColDefs([...priorityCols, ...rest])
    }

    return reuseColDefs(mappedColDefs)
  }, [
    registerCollectionColumns,
    collectionColumns,
    supplyColDefs,
    reuseColDef,
    reuseColDefs,
    identityColumn,
    stockColumn,
    lifecycleColumn,
    salesColumn,
    forecastColumn,
    collectionColumn,
    actionsColumn,
    rulesColumn,
    dynamicColumns,
    extraColumns,
    aggregations,
    priorityColumns,
    extraDataDisplayNameMap,
    leftPinnedColumns,
    permissions.canEdit,
    tableColumnData,
    id,
    defaultColumnIdsToDisplay,
    disabledColumnIdSet,
    alwaysVisibleColumnIdSet,
  ])

  const gridContext = useProductTableGridContext({
    gridRef,
    warehouseId,
    getSkuForecastsBetweenDates,
    alertsUnavailable,
    forecastMetadata,
    netForecastMode,
    netForecastValueGetter,
    netForecastPending,
    forecastAlertThreshold,
    evaluationMap,
    stockColumn,
    forecastColumn,
    versionedColumns: [
      registerCollectionColumns ? versionedCollectionColumns : undefined,
      extraVersionedColumns,
    ],
  })
  const mergedGridContext = useMemo(() => ({ ...gridContext, ...context }), [gridContext, context])

  const { allColumnIds, columnIdsToSelectFrom } = useProductTableColumnIds({
    dynamicColumns,
    extraColumns,
    collectionColumns,
    listCollectionColumns: showCollectionColumns && listCollectionColumnsInChooser,
    gatedColumnIdSet,
    overwriteColumnsToShow,
  })
  const onNewColumnsLoaded = useNewColumnPlacement(
    id,
    dynamicColumns,
    disableColumnSizingPersistence,
  )
  useAggregationFollowsVisibility(id)
  useFollowDefaultColumns(id, defaultColumnIdsToDisplay, defaultColumnIdsExcludedFromAutoAdd)
  const onViewApplied = useViewColumnCheck({
    tableId: id,
    type,
    allColumnIds,
    gatedColumnIdSet,
    listCollectionColumnsInChooser,
    ready: extraDataFields !== undefined && collectionExtraDataFields !== undefined,
  })
  const hideColumn = useHideColumn(gridRef)
  const commitColumns = useCallback(
    (ids: string[]) => commitDisplayedColumns(gridRef.current?.api, ids),
    [gridRef],
  )

  const defaultColumnDef: ColDef<IVariantSummary> = useMemo(
    () => ({
      rowGroup: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: hideHeaderFilterButtons,
      rowGroupIndex: 0,
      wrapHeaderText: true,
      minWidth: 120,
      initialWidth: 120,
      autoHeaderHeight: true,
      // initialFlex, not flex: AG Grid re-applies flex on every columnDefs change, undoing user resizes
      initialFlex: 1,
      enableCellChangeFlash,
      tooltipComponent: CustomAgGridTooltip,
    }),
    [enableCellChangeFlash, hideHeaderFilterButtons],
  )

  const rowSelection = useMemo<RowSelectionOptions | undefined>(() => {
    if (!selectableRows) return undefined
    return {
      mode: "multiRow",
      groupSelects: "descendants",
      checkboxes: (params) => !params.node.footer,
      hideDisabledCheckboxes: true,
      isRowSelectable: isRowSelectable,
      selectAll: "filtered",
      // Checkboxes own this selection model; a row click moves the highlight and releases them.
      // Stated explicitly (ag-grid already defaults it to false) so a click never ticks a row.
      enableClickSelection: false,
    }
  }, [selectableRows, isRowSelectable])

  const emptyStateMessage = useMemo(() => {
    if (aggregations[0] === AggregationMethod.Variant) {
      return "No variants found"
    } else {
      return "No products found"
    }
  }, [aggregations])

  const getMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams<IVariantSummary>) => {
      const colDef = params.column?.getColDef()
      const colId = colDef?.colId ?? ""

      // Calendar columns can't be hidden - don't show hide option
      const isCalendar = isCalendarColumn(colId)

      let menuItems: (DefaultMenuItem | MenuItemDef<IVariantSummary>)[] = isCalendar
        ? [...params.defaultItems]
        : [
            {
              name: "Hide column",
              menuItem: HideColumnItem,
              action: (params) => {
                hideColumn(params.column?.getColId())
              },
              menuItemParams: {
                buttonValue: "Alert",
              },
            },
            "separator",
            ...params.defaultItems,
          ]

      if (colDef?.colId?.includes("extradata") && permissions.canEdit) {
        menuItems = [
          {
            name: "Configure column",
            menuItem: ConfigureExtradataColumn,
            action: () => {
              handleConfigureExtradataColumn(colDef?.colId)
            },
            menuItemParams: {
              buttonValue: "Alert",
            },
          },
          ...menuItems,
        ]
      }
      return menuItems
    },
    [permissions.canEdit, handleConfigureExtradataColumn, hideColumn],
  )

  const finalAggregationOptions = useMemo(() => {
    return aggregationOptions ?? defaultAggregations
  }, [aggregationOptions])

  const secondaryAggregationOptions = useMemo(() => {
    return finalAggregationOptions.filter((agg) => agg !== aggregations[0])
  }, [finalAggregationOptions, aggregations])

  // Mirrors the parent grid's live column order + visibility into the detail grid.
  const buildDetailColumnDefs = useCallback((): ColDef<any>[] => {
    const api = gridRef.current?.api
    const liveState = api?.getColumnState() ?? []

    const colDefByColId = new Map(colDefs.map((c) => [c.colId ?? "", c]))
    const allowedDetailIds = new Set<string>([...detailedSkuColumns, ...(extraDataFields ?? [])])

    // AG Grid 35 does not guarantee getColumnState returns left-pinned before non-pinned,
    // so sort explicitly: left-pinned → non-pinned → right-pinned.
    const parentColIds =
      liveState.length > 0
        ? [...liveState]
            .sort((a, b) => {
              const rank = (p: typeof a.pinned) => (p === "left" ? 0 : p == null ? 1 : 2)
              return rank(a.pinned) - rank(b.pinned)
            })
            .filter((s) => !s.hide && s.colId != null)
            .map((s) => s.colId as string)
        : colDefs
            .filter((c) => !c.initialHide)
            .map((c) => c.colId ?? "")
            .filter(Boolean)

    const filteredColumns = parentColIds
      .map((id) => colDefByColId.get(id))
      .filter((c): c is ColDef<IVariantSummary> => {
        if (!c) return false
        const colId = c.colId ?? ""
        return (
          allowedDetailIds.has(colId) || isStorageLocationColId(colId) || isCalendarColumn(colId)
        )
      })

    // Size split % shows in SKU detail rows regardless of variant-level column selection, unless
    // the company disabled the column.
    const hasSizeSplit = filteredColumns.some(
      (c) => c.colId === ProductTableColumn.SizeSplitConfidence,
    )
    const sizeSplitColumn = colDefByColId.get(ProductTableColumn.SizeSplitConfidence)
    // Second data column (after e.g. Sales), but ahead of period columns: a split between two
    // months reads as belonging to one of them.
    const sizeSplitIndex = isCalendarColumn(filteredColumns[0]?.colId ?? "") ? 0 : 1
    const allFilteredColumns =
      hasSizeSplit || !sizeSplitColumn
        ? filteredColumns
        : filteredColumns.toSpliced(sizeSplitIndex, 0, sizeSplitColumn)
    // Beside period columns (the Availability heatmaps) the split takes their cell padding.
    const besidePeriods = filteredColumns.some((c) => isCalendarColumn(c.colId ?? ""))

    const detailColumns = allFilteredColumns.map((col): ColDef<IVariantSummary> => {
      // leftPinnedColumns is baked into ColDef objects; strip so detail columns are non-pinned.
      // The parent grid's visibility picked these columns, so none starts hidden.
      const base: ColDef<IVariantSummary> = { ...col, pinned: undefined, initialHide: undefined }
      if (col.colId === ProductTableColumn.SizeSplitConfidence) {
        const sizeSplitNaming = purchaseOrderColumnNaming[PurchaseOrderTableColumn.SizeSplit]
        return {
          ...base,
          headerName: sizeSplitNaming.columnTitle,
          headerTooltip: sizeSplitNaming.tooltip,
          valueGetter: (params: any) => params.data?.sizeSplitWeight,
          valueFormatter: undefined,
          headerClass: "ag-right-aligned-header",
          cellClass: cn("ag-cell-vcenter [&_.ag-cell-value]:w-full!", besidePeriods && "px-1!"),
          tooltip: (params: any) => {
            const fraction = params.data?.skuStockoutImputedFraction
            if (!fraction) return undefined
            return `${Math.round(fraction * 100)}% of sales is estimated due to past stockouts`
          },
          cellRenderer: (params: any) => {
            const weight = params.data?.sizeSplitWeight
            const fraction = params.data?.skuStockoutImputedFraction
            const hasWarning = fraction != null && fraction > IMPUTATION_WARNING_THRESHOLD
            if (weight == null) {
              return <div className="flex w-full justify-end text-fg-muted">—</div>
            }
            return (
              <div className="flex w-full items-center justify-end gap-1 tabular-nums">
                <span>{(weight * 100).toFixed(1)}%</span>
                {hasWarning && (
                  <Warning size={12} weight="fill" className="text-yellow-400 shrink-0" />
                )}
              </div>
            )
          },
          aggFunc: undefined,
        }
      }
      return base
    })

    return [...skuIdentityColumnDefs(), ...detailColumns]
  }, [colDefs, extraDataFields, gridRef])

  // Keep already-open detail grids in sync when the parent grid's columns change.
  // detailCellRendererParams only runs on row open, so changes after that need this.
  useEffect(() => {
    const api = gridRef.current?.api
    // Custom detail grids own their columns; the default SKU columns would overwrite them.
    if (!api || overrideDetailCellRendererParams || detailCellRenderer) return
    const refreshOpenDetailGrids = (event: any) => {
      if (event?.finished === false) return // skip intermediate columnMoved events
      const newDefs = buildDetailColumnDefs()
      const nonPinnedIds = newDefs
        .filter((col) => col.pinned !== "left")
        .map((col) => col.colId ?? (col.field as string) ?? "")
        .filter(Boolean)
      api.forEachDetailGridInfo((info) => {
        if (!info.api) return
        info.api.updateGridOptions({ columnDefs: newDefs })
        // moveColumns enforces the order; updateGridOptions alone preserves existing order.
        info.api.moveColumns(nonPinnedIds, 1)
      })
    }
    api.addEventListener("columnMoved", refreshOpenDetailGrids)
    api.addEventListener("columnVisible", refreshOpenDetailGrids)
    api.addEventListener("columnPinned", refreshOpenDetailGrids)
    return () => {
      if (api.isDestroyed?.()) return
      api.removeEventListener("columnMoved", refreshOpenDetailGrids)
      api.removeEventListener("columnVisible", refreshOpenDetailGrids)
      api.removeEventListener("columnPinned", refreshOpenDetailGrids)
    }
  }, [
    buildDetailColumnDefs,
    gridRef,
    isGridReady,
    overrideDetailCellRendererParams,
    detailCellRenderer,
  ])

  const canExpandAllSku = !!masterDetail && !overrideDetailCellRendererParams && !detailCellRenderer
  const { detailCellRendererParams, collapseAllMasterRows, skuMenuItems } = useSkuMasterDetail({
    gridRef,
    masterDetail,
    canExpandAllSku,
    warehouseId,
    channel,
    defaultColumnDef,
    buildDetailColumnDefs,
    searchText,
    searchBy,
    localFilters,
  })

  const processCellCallback = useCallback(
    (params: ProcessCellForExportParams<IVariantSummary>) => {
      if (isAutoGroupColumn(params.column)) return rowLabel(params.node, aggregations)
      // Fabric rows in the greige plan carry comments of their own.
      if (params.column.getColId() === SupplyTableColumn.Comments) {
        return groupRowExportValue(params)
      }
      if (params.value && typeof params.value === "object" && "value" in params.value) {
        return params.value.value
      }
      // Arrays of named objects (e.g. tags) — export as comma-separated names
      if (
        Array.isArray(params.value) &&
        params.value.length > 0 &&
        params.value.every((v: unknown) => v && typeof v === "object" && "name" in v)
      ) {
        return params.value.map((v: { name: string }) => v.name).join(", ")
      }
      return params.value
    },
    [aggregations],
  )

  const processRowGroupCallback = useCallback(
    (params: ProcessRowGroupForExportParams<IVariantSummary>) =>
      rowLabel(params.node, aggregations),
    [aggregations],
  )

  const exportParams = useMemo(
    () => ({ processCellCallback, processRowGroupCallback }),
    [processCellCallback, processRowGroupCallback],
  )

  // Default SKU detail path (not a custom renderer, no explicit detailRowAutoHeight).
  const autoSizeSkuDetail =
    !!masterDetail &&
    !overrideDetailCellRendererParams &&
    !detailCellRenderer &&
    !detailRowAutoHeight

  const defaultGridOptions: GridOptions<IVariantSummary> = useMemo(
    () => ({
      onTooltipShow: (params) => {
        params?.parentGui?.classList.add("ag-tooltip-active")
      },
      onTooltipHide: (params) => {
        params?.parentGui?.classList.remove("ag-tooltip-active")
      },
      getRowId: (params) =>
        params.data.variantId +
        (params.data.groupVendor ? params.data.groupVendor : "") +
        (params.data.groupCollection ? params.data.groupCollection : ""),
      defaultExcelExportParams: exportParams,
      defaultCsvExportParams: exportParams,
      detailCellRendererParams: overrideDetailCellRendererParams ?? detailCellRendererParams,
      onColumnVisible: clearHiddenColumnSortAndFilter,
      // Cell focus is the single input for the highlight, so a mouse click, the arrow keys and a
      // programmatic setFocusedCell all move it through one path. The checkbox column is excluded:
      // ticking a checkbox focuses its cell, and the tick already moves the highlight through the
      // selection — highlighting the one row here would then release the very selection it made.
      onCellFocused: highlightOnRowClick
        ? (event) => {
            if (event.rowIndex == null || event.rowPinned) return
            const colId = typeof event.column === "string" ? event.column : event.column?.getColId()
            if (colId === SELECTION_COLUMN_ID) return
            // Some controls act on the row without asking the panel to detail it — see
            // NO_HIGHLIGHT_ATTR.
            if (document.activeElement?.closest?.(`[${NO_HIGHLIGHT_ATTR}]`)) return
            const node = event.api.getDisplayedRowAtIndex(event.rowIndex)
            if (!node || node.group || node.footer || node.data == null || node.id == null) return
            highlightRows(node.id)
          }
        : undefined,
      // Only runs when ag-grid creates a row (scroll, sort, filter); `highlightRows` owns the
      // click path.
      getRowClass: highlightOnRowClick
        ? (params) =>
            params.node.id != null && highlightedIdsRef.current.has(params.node.id)
              ? HIGHLIGHT_ROW_CLASS
              : undefined
        : undefined,
    }),
    [
      exportParams,
      overrideDetailCellRendererParams,
      detailCellRendererParams,
      highlightOnRowClick,
      highlightRows,
      highlightedIdsRef,
    ],
  )

  const mergedGridOptions = useMemo(
    () => ({ ...defaultGridOptions, ...customGridOptions }),
    [defaultGridOptions, customGridOptions],
  )

  const filteredColumnDefs = useMemo(() => {
    const visible = colDefs.filter((x) => x.colId && !gatedColumnIdSet.has(x.colId))
    if (!enableNumberFilters) return visible
    return visible.map((col) =>
      col.cellDataType === "number" && (col.filter === undefined || col.filter === false)
        ? { ...col, filter: "agNumberColumnFilter" }
        : col,
    )
  }, [colDefs, gatedColumnIdSet, enableNumberFilters])

  // Folds consecutive colDefs carrying the same `context.headerGroup.groupId` into a ColGroupDef.
  // Applied LAST, after every flat-pipeline concern (visibility, `::` suffix mapping, saved column
  // state, chooser ids, detail-grid sync) — those all operate on leaf colIds and stay untouched.
  const groupedColumnDefs = useMemo((): (
    | ColDef<IVariantSummary>
    | ColGroupDef<IVariantSummary>
  )[] => {
    const out: (ColDef<IVariantSummary> | ColGroupDef<IVariantSummary>)[] = []
    for (const col of filteredColumnDefs) {
      const marker = (col.context as { headerGroup?: HeaderGroupMarker } | undefined)?.headerGroup
      if (!marker) {
        out.push(col)
        continue
      }
      const prev = out[out.length - 1]
      if (prev && "groupId" in prev && prev.groupId === marker.groupId) {
        prev.children.push(col)
      } else {
        // No marryChildren: saved views from before a consumer adopted grouping may interleave a
        // group member with outside columns, and married children would silently deviate from that
        // saved order on restore. Without it AG Grid splits the group header instead — faithful to
        // the saved state.
        out.push({
          groupId: marker.groupId,
          headerName: marker.headerName,
          headerTooltip: marker.headerTooltip,
          children: [col],
        })
      }
    }
    return out.some((c) => "children" in c) ? out : filteredColumnDefs
  }, [filteredColumnDefs])

  const finalInitialSortColumn = useMemo(
    () => initialSortColumn ?? { name: ProductTableColumn.ProductName, direction: "asc" },
    [initialSortColumn],
  )

  const getContextMenuItems = useCallback(
    (
      params?: GetContextMenuItemsParams<IVariantSummary>,
    ): (DefaultMenuItem | MenuItemDef<IVariantSummary>)[] => {
      const node = params?.node
      const items: (DefaultMenuItem | MenuItemDef<IVariantSummary>)[] = []

      // The row's own actions. A group stands for its leaves, so right-clicking one acts on every
      // variant under it; a leaf stands for itself (which `getFilteredLeafChildren` handles too).
      const leafNodes = getFilteredLeafChildren(node)
      const entries = filterDefined(leafNodes.map((leaf) => leaf.data))
      if (entries.length > 0) {
        const rowIds = leafRowIds(leafNodes)
        // Counted by variant, not by row: the highlight publishes deduped variant ids, so counting
        // rows would promise more than the inspector shows wherever a variant spans several groups.
        const variantCount = new Set(entries.map((entry) => entry.variantId)).size
        // Only where there is an inspector to open and this table drives its highlight — the same
        // pair the in-cell peek button gates on, so the menu never offers a dead action.
        if (peekEnabled && rowIds.length > 0) {
          items.push({
            name: variantCount === 1 ? "Inspect" : `Inspect ${variantCount} variants`,
            // The same glyph the in-cell peek button and the inspector's toolbar toggle use, so the
            // three ways to reach the inspector read as one action.
            icon: menuIcon(<SidebarSimple size={16} />),
            action: () => {
              highlightRows(rowIds)
              openInspector()
            },
          })
        }
        // Omitted rather than disabled when the variants span products: the product page is
        // addressed by ONE productId (`/product/$productId?variantIds=…`), and an ag-grid menu row
        // has no tooltip surface to explain a disabled entry — so the deliberate exception to
        // "disable, don't hide" (same reasoning as CollectionManagementList's remove action).
        if (new Set(entries.map((entry) => entry.productId)).size === 1) {
          items.push({
            name: "Open product page",
            // Matches the inspector header's "open variant" action, which goes to the same place.
            icon: menuIcon(<ArrowSquareOut size={16} />),
            action: () => openForecastView(entries, false),
          })
        }
        items.push("separator")
      }

      items.push(...skuMenuItems(node))
      return items
    },
    [skuMenuItems, peekEnabled, highlightRows, openInspector, openForecastView],
  )

  // Collapse synchronously before the state change so ag-grid removes the
  // detail row before re-diffing rowData; otherwise the master can be filtered
  // out while its detail row is left orphaned (HAK-1679).
  const handleSetSearchText = useCallback(
    (value: string) => {
      if (masterDetail) collapseAllMasterRows()
      setSearchText(value)
    },
    [masterDetail, collapseAllMasterRows, setSearchText],
  )

  const handleSetSearchBy = useCallback(
    (field: string) => {
      if (masterDetail) collapseAllMasterRows()
      setSearchField(field)
    },
    [masterDetail, collapseAllMasterRows, setSearchField],
  )

  const handleSetLocalFilters = useCallback(
    (filters: Filter[]) => {
      if (masterDetail) collapseAllMasterRows()
      setLocalFilters(filters)
    },
    [masterDetail, collapseAllMasterRows, setLocalFilters],
  )

  const handleGridReady = useCallback(
    (event: GridReadyEvent<IVariantSummary>) => {
      setIsGridReady(true)
      onGridReady?.(event)
    },
    [onGridReady],
  )

  // Keyboard shortcut for expand/collapse functionality
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + E to toggle expand/collapse
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === "e" &&
        onIsExpandedChange &&
        isExpanded !== undefined
      ) {
        event.preventDefault()
        trackEvent("Product Table Expand Toggled", {
          Source: "Keyboard Shortcut",
          State: isExpanded ? "Collapsed" : "Expanded",
          Shortcut: "Ctrl+E",
        })
        onIsExpandedChange(!isExpanded)
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onIsExpandedChange, isExpanded])

  useOpenColumnChooserCommand(id)
  useRegisterTable(id, {
    aggregationOptions: finalAggregationOptions,
    aggregationValue: aggregations,
    setAggregations,
    aggregationMeta: {
      extraDataFields: extraDataFields ?? [],
      collectionExtraDataFields: collectionExtraDataFields ?? [],
      hasCollectionContext: !!effectiveCollectionIds,
    },
    columnChooserProps: {
      owner: "grid",
      columnIds: columnIdsToSelectFrom,
      setDisplayedColumnIds: commitColumns,
      defaultColumnIds: defaultColumnIdsToDisplay,
      fallbackColumnTitles: extraColumnTitles,
    },
    onViewApplied,
    search: enableSearch
      ? {
          options: getProductTableSearchFields(),
          onBeforeChange: masterDetail ? collapseAllMasterRows : undefined,
        }
      : undefined,
  })
  const displayControlsHoisted = useTableHasConsumer(id)

  const showAggregation = finalAggregationOptions.length > 1 && !displayControlsHoisted
  const showColumnChooser = !displayControlsHoisted
  const showLocalFilter = enableFilter && !displayControlsHoisted
  const showInlineSearch = enableSearch && !displayControlsHoisted
  const showExpandButton = onIsExpandedChange && isExpanded !== undefined
  const showToolbar =
    showAggregation ||
    showColumnChooser ||
    showLocalFilter ||
    showInlineSearch ||
    showExpandButton ||
    !!topLeftComponent ||
    !!topRightComponent

  return (
    <div style={{ height: height ?? "100%" }} className="w-full flex flex-col gap-3">
      {highlightOnRowClick && isGridReady && (
        <SelectionHighlightSync fromSelection={fromSelection} />
      )}
      <MultiEditPanel
        type={type}
        collectionIds={collectionIds}
        tabContext={tabContext}
        hideReorder={hideReorder}
        hideAdjust={hideAdjust}
        delayedPoWarningMap={delayedPoWarningMap}
        snoozedUntilMap={snoozedUntilMap}
        onReviewSelected={onReviewSelected}
      />
      {showToolbar && (
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-2">
            {showAggregation && (
              <>
                <span className="label-base">Group rows by</span>
                <AggregationSelector
                  prefix={""}
                  selectedMethod={aggregations[0]!}
                  aggregationMethods={finalAggregationOptions}
                  dynamicAggregationMethods={extraDataFields}
                  collectionDynamicAggregationMethods={
                    effectiveCollectionIds ? collectionExtraDataFields : undefined
                  }
                  onSelect={(method) => {
                    handleAggregationChange(method as AggregationMethod)
                  }}
                />
                {aggregations.length > 1 &&
                  aggregations[0] !== AggregationMethod.ProductName &&
                  aggregations[0] !== AggregationMethod.Variant && (
                    <AggregationSelector
                      title="and "
                      prefix=""
                      selectedMethod={aggregations[1]!}
                      aggregationMethods={secondaryAggregationOptions}
                      dynamicAggregationMethods={extraDataFields}
                      collectionDynamicAggregationMethods={
                        effectiveCollectionIds ? collectionExtraDataFields : undefined
                      }
                      onSelect={(method) => {
                        handleAggregationChange(method as AggregationMethod, 1)
                      }}
                    />
                  )}
              </>
            )}
            {topLeftComponent}
          </div>
          <div className="flex flex-row items-center gap-2">
            {topRightComponent}
            {showColumnChooser && (
              <ProductTableColumnChooser
                tableId={id}
                columnIds={columnIdsToSelectFrom}
                setDisplayedColumnIds={commitColumns}
                defaultColumnIds={defaultColumnIdsToDisplay}
                fallbackColumnTitles={extraColumnTitles}
              />
            )}
            {showLocalFilter && (
              <FilterListPopover
                filters={localFilters}
                onFiltersChange={handleSetLocalFilters}
                isLocal={true}
                extraFilterFields={extraFilterFields}
                extraFilterOptions={extraFilterOptions}
                trigger={
                  localFilters.length === 0 ? (
                    <IconButton size="sm" variant="neutral">
                      <FunnelSimple />
                    </IconButton>
                  ) : (
                    <Button
                      size="sm"
                      variant="neutral"
                      className="bg-background-highlight shadow-button-primary hover:bg-background-highlight-hover"
                    >
                      <FunnelSimple />
                      {localFilters.length}
                    </Button>
                  )
                }
              />
            )}
            {showInlineSearch && (
              <ExpandableSearchField
                initialSearchText={searchText}
                onSearchTextChange={handleSetSearchText}
                placeholder="Search..."
                searchBy={searchBy}
                onSearchByChange={handleSetSearchBy}
                searchByOptions={getProductTableSearchFields()}
              />
            )}
            {showExpandButton && (
              <Tooltip
                title={`${isExpanded ? "Collapse" : "Expand"} (${isMacOs ? "⌘E" : "Ctrl + E"})`}
              >
                <IconButton
                  size="sm"
                  variant="neutral"
                  onClick={() => {
                    trackEvent("Product Table Expand Toggled", {
                      Source: "Click",
                      State: isExpanded ? "Collapsed" : "Expanded",
                      Shortcut: "",
                    })
                    onIsExpandedChange?.(!isExpanded)
                  }}
                >
                  {isExpanded ? <ArrowsIn /> : <ArrowsOutSimple />}
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      <DataTableBaseV2<IVariantSummary>
        id={id}
        gridRef={gridRef}
        rowIdField={customRowFieldId ?? "variantId"}
        rowData={transformedData}
        gridOptions={mergedGridOptions}
        enableRowPinning={enableRowPinning}
        isRowPinned={isRowPinned}
        grandTotalRow={hasDuplicatedVendorRows ? undefined : grandTotalRowPosition}
        defaultColumnDef={defaultColumnDef}
        autoGroupColumnDef={autoGroupColumnDef}
        rowSelection={rowSelection}
        onSelectionChanged={handleSelectionChangedLocal ?? undefined}
        columnDefs={groupedColumnDefs}
        initialSortColumn={finalInitialSortColumn}
        defaultFilterModel={defaultFilterModel}
        emptyMessage={emptyStateMessage}
        getMainMenuItems={getMainMenuItems}
        getContextMenuItems={getContextMenuItems}
        masterDetail={masterDetail}
        detailCellRendererParams={overrideDetailCellRendererParams ?? detailCellRendererParams}
        detailCellRenderer={detailCellRenderer}
        detailRowAutoHeight={autoSizeSkuDetail ? true : detailRowAutoHeight}
        maxDetailRowHeight={autoSizeSkuDetail ? MAX_DETAIL_ROW_HEIGHT : undefined}
        keepDetailRows={keepDetailRows}
        loading={loading || isLoadingChannelData}
        groupAggFiltering={groupAggFiltering}
        minHeight={minHeight}
        onSortChanged={onSortChanged}
        onFilterChanged={onFilterChanged}
        onGridReady={handleGridReady}
        onNewColumnsLoaded={onNewColumnsLoaded}
        disableColumnSizingPersistence={disableColumnSizingPersistence}
        startOnFirstPage={startOnFirstPage}
        migrateSavedState={migrateProductTableState}
        restoreAggregation={reconcileAggregation}
        aggFuncs={PRODUCT_TABLE_AGG_FUNCS}
        context={mergedGridContext}
        cardClassName={cardClassName}
        footerClassName={footerClassName}
        cardFooter={cardFooter}
      />
      <ConfigureExtradataDialog
        dialogOpen={extradataDialogOpen}
        setDialogOpen={setExtradataDialogOpen}
        extradataConfiguration={extradataDialogColumn}
        updateExtradataConfiguration={handleUpdateExtradataConfiguration}
      />
    </div>
  )
}
